/**
 * 富山県の料理12品。
 *
 * 富山湾と昆布で組み立てた。
 * ホタルイカ・白えび・バイ貝・ブリ・タラ・サクラマスで海から六品、
 * 昆布で三品（とろろ昆布のおにぎり・昆布〆・昆布巻き）、
 * 残りを酢ずき・べっこう・焼き付けの山側と行事の料理で埋めている。
 *
 * ますのすしは丸い。
 *  農水省の「にっぽん伝統食図鑑」に「曲げ物（曲げわっぱ）の底に笹を放射状に敷き」とある。
 *  山口の岩国寿司・和歌山のこけら寿司・奈良と鳥取の柿の葉寿司はどれも四角い押し寿司なので、
 *  ここだけは丸い曲物と放射状の笹をはっきり描かせて、絵がぶつからないようにする。
 *
 * 他県と重ならないように外したもの。
 *  - カニ … 兵庫・鳥取・福井・佐賀ですでに4県
 *  - 富山ブラック … ラーメンはすでに6県
 *  - かぶらずし … 石川に割り当てた
 *  - いとこ煮 … 山口の「いとこ煮」と同じ名前
 *  - 黒豆おこわ（みたま） … 石川にも同じものがあり、弔事の料理でもある
 *  - そば・そうめん・和牛 … すでに他県に多い
 *
 * ブリは富山に割り当てた。ぶり大根のほか、ぶりしゃぶ・かぶらずしにも使われるが、
 * 12品のうち1品にとどめている。
 *
 * べっこう（卵の寒天寄せ）は石川では「えびす」と呼ぶ同じ料理。富山に置いた。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：白えびのかき揚げ・べっこう・焼き付け・酢ずき・昆布巻き・ぶり大根（店で食べられる品に差し替え）
 * - 氷見うどん：歴史・由来（いつ・どこから伝わったか）は、開いた公式ページに書かれていないので書かない
 * - 氷見うどん：麺の太さ（『細い』以上の具体的な数字）は公式で確かめていないので書かない
 * - げんげの唐揚げ：唐揚げの作り方（衣・下ごしらえ）は公式資料にないので書かない。農林水産省のページは味噌汁の作り方で、唐揚げは『唐揚げ、天ぷら、一夜干しとして提供する飲食店が増加』とだけある
 * - 富山ブラック：発祥の店名は書かない（店の宣伝になる。富山市観光協会の特集も歴史の部分では店名を出していない）
 * - 富山ブラック：考案の年を『1947年』と書くページ（とやま観光ナビ総集編）と『1940年代後半』と書くページ（富山市観光協会）があるので、幅のある『1940年代後半』にした
 * - 紅ズワイガニ：富山市観光協会『富山湾の寿司ネタ』の紅ズワイガニの項の旬（11月6日〜1月20日）は、本ズワイを含むズワイガニ全体の表記と読めるので使わない
 * - 紅ズワイガニ：『極上 高志の紅ガニ』のタグなど等級の話は、出典で基準まで確かめていないので書かない
 * - 白えびの刺身（白えび刺身丼）：『富山湾の宝石』は出典が『〜と称される』『まるで宝石のよう』と書いているので headline に使った。言い切りの称号としては description に入れていない
 * - 白えびの刺身（白えび刺身丼）：刺身丼という食べ方は公式資料で確かめていないので書かない
 * - 品名「富山湾鮨」を「富山湾の地魚の握り」に地元で通じる呼び名に改めた（2026-10-11 地元民レビュー。同じ料理なので ID はそのまま）。公式名は説明文に残した
 * - 富山湾鮨：値段の決まり（2,700〜5,500円）は変わりうる情報なので書かない
 * - 富山湾鮨：予約特典は店ごとに違い変わりうるので書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { toyamaSceneryTour } from './toyamaScenes';
import { TOYAMA_FOOD_HULLS } from './toyamaFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'tororokombu-onigiri',
    level: 1,
    name: 'とろろ昆布のおにぎり',
    shortName: 'とろろ昆布のおにぎり',
    image: '/assets/foods/toyama/01_tororokombu_onigiri.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '海苔ではなく、黒いとろろで覆う。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/37_7_toyama.html',
    icon: { label: 'とろろ昆布のおにぎり', shape: 'mochi', baseColor: '#3a3428', accentColor: '#f0ece0' },
    reward: {
      unlockScore: 200,
      area: '富山市・黒部市',
      headline: '白米のおにぎりを、海苔ではなく削ったとろろ昆布でふんわり覆った富山の定番。',
      tip: '江戸時代の北前船で富山に寄港した船から北海道産の昆布が大量に購入された。明治時代には北海道に移住した富山県民が故郷の親類に昆布を送る交流もあった。現在もほぼ全量が北海道産。長期保存ができるため、とろろ昆布はほとんどの家庭で常備されている。海苔と違って時間が経っても柔らかい。',
      links: searchLinks('富山市', 'とろろ昆布 おにぎり'),
    },
  },
  {
    id: 'hotaruika-sumiso',
    level: 2,
    name: 'ほたるいかの酢味噌和え',
    shortName: 'ほたるいかの酢味噌和え',
    image: '/assets/foods/toyama/02_hotaruika_sumiso.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: 'ゆでると、胴が丸くなる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/37_1_toyama.html',
    icon: { label: 'ほたるいかの酢味噌和え', shape: 'roe', baseColor: '#c8a0a8', accentColor: '#a07838' },
    reward: {
      unlockScore: 500,
      area: '滑川市',
      headline: '産卵期に沿岸へ集まるホタルイカをゆで、ワケギと生ワカメとともに酢味噌で和えたもの。',
      tip: '江戸時代から滑川漁港でホタルイカ漁が行われてきた。富山湾のホタルイカ群遊海面は国の特別天然記念物に指定されている。産卵期の三月に沿岸へ集まるところを定置網で獲る。漁は三月の解禁から六月ごろまで。ゆでると胴がまるくなりつやが出て、中はトロッとして外はプリプリの食感になる。',
      links: searchLinks('滑川市', 'ほたるいか'),
    },
  },
  {
    id: 'bai-no-nimono',
    level: 3,
    name: 'ばいの煮もの',
    shortName: 'ばいの煮もの',
    image: '/assets/foods/toyama/03_bai_no_nimono.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: 'めでたいことが倍になる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/37_16_toyama.html',
    icon: { label: 'ばいの煮もの', shape: 'berry', baseColor: '#8a7a58', accentColor: '#5a3a20' },
    reward: {
      unlockScore: 1000,
      area: '富山県内全域',
      headline: '富山湾の深海にすむ小ぶりのバイ貝を、殻ごとだしと醤油で十分ほど煮た祝いの一品。',
      tip: 'めでたいことが倍になるという語呂合わせから、お祝いの席や祭事の宴に登場する食材。漁獲量の多い小ぶりなツバイを使い、「つんこい（小さい）バイ」という方言が名の由来とされる。茹でて洗ってから、合わせだしで十分ほど煮込む。',
      links: searchLinks('富山市', 'ばい貝 煮もの'),
    },
  },
  {
    id: 'himi-udon',
    level: 4,
    name: '氷見うどん',
    shortName: '氷見うどん',
    image: '/assets/foods/toyama/04_himi_udon.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '手打ちした生地を何度も引き伸ばし、細く長くする「手延べ」という製法でつくるうどん。手打ちによる強いコシと、もちもちとした弾力、つるりとした喉ごしが持ち味。',
    source:
      'https://www.info-toyama.com/stories/toyama-gourmet',
    icon: { label: '氷見うどん', shape: 'noodle-bowl', baseColor: '#f3eee2', accentColor: '#b98a4a' },
    reward: {
      unlockScore: 1800,
      area: '氷見市',
      headline: '引き伸ばしてつくる細いうどん',
      tip: 'ざるで冷たく食べるほか、釜揚げにしてもおいしい。氷見市観光協会は、つるりとした喉ごしとコシの強さに熱烈なファンが多いと紹介している。',
      links: searchLinks('氷見市', '氷見うどん'),
      official: [{ label: '氷見市観光協会「氷見のうどん」', url: 'https://www.kitokitohimi.com/site/gourmet/364.html' }],
    },
  },
  {
    id: 'genge-karaage',
    level: 5,
    name: 'げんげの唐揚げ',
    shortName: 'げんげの唐揚げ',
    image: '/assets/foods/toyama/05_genge_karaage.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '深海魚のゲンゲを唐揚げにする。細長い体は白く透明感があり、身は分厚い。かつては甘エビ漁の混獲物で「下の下の魚」と呼ばれたが、コラーゲンの豊富さや身のおいしさが見出された。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/37_27_toyama.html',
    icon: { label: 'げんげ', shape: 'fish', baseColor: '#e2c27a', accentColor: '#f4f0e6' },
    reward: {
      unlockScore: 3000,
      area: '富山市・射水市・魚津市',
      headline: '下の下の魚から幻の魚へ',
      tip: 'めったに出会えない幻の魚として、今は「幻魚」と書く。漁期は9月から翌年5月。石川ではゲンゲンボウ、福井ではミズベコと呼ばれる。',
      links: searchLinks('富山市', 'げんげの唐揚げ'),
    },
  },
  {
    id: 'toyama-black',
    level: 6,
    name: '富山ブラック',
    shortName: '富山ブラック',
    image: '/assets/foods/toyama/06_toyama_black.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '真っ黒に見えるスープは、濃厚でコクのある醤油だれによるもの。白ごはんやおにぎりと一緒に食べることを前提に、塩味とうまみを強くしてある。ネギと黒胡椒が味の決め手。',
    source:
      'https://www.toyamashi-kankoukyoukai.jp/feature/feature-toyama-black-ramen-10/',
    icon: { label: '富山ブラック', shape: 'ramen', baseColor: '#1e1410', accentColor: '#5a7a3a' },
    reward: {
      unlockScore: 4500,
      area: '富山市',
      headline: 'ごはんと食べる黒いスープ',
      tip: '戦後まもない1940年代後半、復興期の富山市で生まれた。復興に携わる肉体労働者が汗をかいた後に塩分を補えるよう、醤油を効かせた濃い味付けにしたのが始まりとされる。',
      links: searchLinks('富山市', '富山ブラック'),
      official: [{ label: 'とやま観光ナビ「【富山グルメ総集編】おいしい富山の名物グルメを堪能しよう！」', url: 'https://www.info-toyama.com/stories/toyama-gourmet' }],
    },
  },
  {
    id: 'tarajiru',
    level: 7,
    name: 'たら汁',
    shortName: 'たら汁',
    image: '/assets/foods/toyama/07_tarajiru.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '一匹まるごと、ぶつ切りで入れる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/37_2_toyama.html',
    icon: { label: 'たら汁', shape: 'hotpot', baseColor: '#6a5a3a', accentColor: '#e8e4d8' },
    reward: {
      unlockScore: 6500,
      area: '朝日町',
      headline: 'スケソウダラを頭も肝も白子も真子も丸ごとぶつ切りにし、ごぼうとねぎで味噌仕立てにした大鍋。',
      tip: 'かつてタラの水揚げが豊富だった朝日町で、漁から帰る男たちを迎えるため、漁師の女房たちが流木を集め大鍋を沸かし、味噌とタラを入れて煮込んだのがはじまり。浜辺で大鍋を囲み円座になって食べられていた。現在は「たら汁街道」と呼ばれる国道8号周辺に提供店が並ぶ。白子や真子が成長する十二月から二月頃は旨みがより増す。',
      links: searchLinks('朝日町', 'たら汁'),
    },
  },
  {
    id: 'kobujime',
    level: 8,
    name: '昆布〆',
    shortName: '昆布〆',
    image: '/assets/foods/toyama/08_kobujime.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '昆布に挟まれて、身が透きとおる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/37_12_toyama.html',
    icon: { label: '昆布〆', shape: 'sashimi-platter', baseColor: '#2a4028', accentColor: '#f0ece8' },
    reward: {
      unlockScore: 9000,
      area: '富山県内全域',
      headline: '白身魚の刺身を昆布で挟んで一晩おき、昆布の旨味を移した富山の保存の知恵。',
      tip: '江戸時代に北前船が北海道から運んだ昆布と富山湾の魚の組み合わせで生まれた。冷蔵庫のない時代、昆布が魚の余分な水分を吸収して日持ちをよくした。富山で使う昆布の大半は北海道産の羅臼昆布。カジキ（サスと呼ばれる）・タイ・ヒラメなどの白身魚のほか、シロエビやホタルイカも締める。',
      links: searchLinks('富山市', '昆布〆'),
    },
  },
  {
    id: 'masu-no-sushi',
    level: 9,
    name: 'ますのすし',
    shortName: 'ますのすし',
    image: '/assets/foods/toyama/09_masu_no_sushi.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '丸い曲物に、笹を放射状に敷く。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/traditional-foods/menu/masu_zusi.html',
    icon: { label: 'ますのすし', shape: 'sashimi-platter', baseColor: '#e08a8a', accentColor: '#4a7a38' },
    reward: {
      unlockScore: 12000,
      area: '富山市',
      headline: '丸い曲物の底に笹を放射状に敷き、酢飯の上に酢締めのサクラマスを円く並べて重石をした押し寿司。',
      tip: '曲げ物の底に笹を放射状に敷き、味付けした酢飯を均一に広げ、塩漬けにして酢で締めた薄切りのサクラマスを型に合わせて並べ、笹を順番に折りたたんで蓋をする。それを何段も重ねて上から重石を乗せ、数時間置いて仕上げる。一段重ねたものと二段重ねたものがある。江戸時代の1717年、富山藩士が神通川のアユで寿しを作ったのが始まりで、のちにサクラマスになった。',
      links: searchLinks('富山市', 'ますのすし'),
    },
  },
  {
    id: 'beni-zuwaigani',
    level: 10,
    name: '紅ズワイガニ',
    shortName: '紅ズワイガニ',
    image: '/assets/foods/toyama/10_beni_zuwaigani.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '水深800〜1,400mほどの深い海にすむカニ。もともと紅色の殻は、ゆでるといっそう鮮やかになる。富山県産の紅ズワイガニには「高志の紅ガニ」という愛称が付けられている。',
    source:
      'https://www.info-toyama.com/stories/shinminato-kanigoya',
    icon: { label: '紅ズワイ', shape: 'sashimi-platter', baseColor: '#c8321e', accentColor: '#f2d9c4' },
    reward: {
      unlockScore: 16000,
      area: '射水市（新湊）',
      headline: 'ゆでると紅色が深まる深海のカニ',
      tip: '漁場は新湊漁港の沖合約10km。漁は9月1日に解禁され、旬は9月から4月ごろ。新湊漁港では朝と昼の1日2回セリがあり、昼セリでは床一面が紅色に埋まる。',
      links: searchLinks('射水市', '紅ズワイガニ'),
    },
  },
  {
    id: 'shiroebi-sashimi',
    level: 11,
    name: '白えびの刺身（白えび刺身丼）',
    shortName: '白えびの刺身',
    image: '/assets/foods/toyama/11_shiroebi_sashimi.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '体長5〜8cmほどのシロエビの殻をむいて刺身にする。水揚げ直後は半透明の淡いピンク色に輝く。甘みが強い反面、鮮度が落ちやすい。機械でむくと身がつぶれるため、手むきが主流だ。',
    source:
      'https://www.info-toyama.com/stories/shiroebi',
    icon: { label: '白えび刺身', shape: 'sashimi-platter', baseColor: '#f4d4d0', accentColor: '#5a8a3a' },
    reward: {
      unlockScore: 22000,
      area: '射水市・富山市',
      headline: '殻を手でむく富山湾の宝石',
      tip: '漁が成り立つほどとれるのは全国でも富山だけとされ、新湊漁港と岩瀬漁港が主な水揚げ港。冷凍技術の向上でむき身にしやすくなり、刺身や寿司ネタとして人気が出た。',
      links: searchLinks('富山市', '白えび 刺身'),
      official: [{ label: '農林水産省「うちの郷土料理 白えびのかき揚げ 富山県」', url: 'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/37_9_toyama.html' }],
    },
  },
  {
    id: 'toyamawan-sushi',
    level: 12,
    name: '富山湾の地魚の握り',
    shortName: '地魚の握り',
    image: '/assets/foods/toyama/12_toyamawan_sushi.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '県内の店が「富山湾鮨」の名で出す握り。富山湾でとれた旬の地魚を、富山県産米のシャリで握った寿司10貫に、富山らしい汁物をつけて出すと決まっている。注文を受けてから握り始め、ネタの名前を一貫ずつ教えてもらえる。',
    source:
      'https://www.info-toyama.com/stories/toyamawan-sushi',
    icon: { label: '富山湾鮨', shape: 'sashimi-platter', baseColor: '#f2ece0', accentColor: '#d8644a' },
    reward: {
      unlockScore: 30000,
      area: '富山県内各地',
      headline: '富山湾の地魚で握る10貫',
      tip: '多種多様な魚がすむ富山湾は「天然のいけす」と呼ばれ、季節によって寿司ネタが変わる。白エビは4月から11月がネタの時期とされる。',
      links: searchLinks('富山市', '富山湾鮨'),
      official: [{ label: '富山市観光協会「富山湾の寿司ネタ - すしのまち とやま」', url: 'https://www.toyamashi-kankoukyoukai.jp/sushinomachitoyama/type/' }],
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const toyamaFoodStage: StageDefinition = {
  regionId: 'toyama',
  regionName: '富山県',
  mode: 'food',
  title: '富山めしマージ',
  theme: {
    primaryColor: '#2a5a8a',
    secondaryColor: '#d88a90',
    accentColor: '#e8c0c0',
    backgroundColor: '#0e131c',
  },
  /** 背景ツアー「富山めぐり」。軸は一日の時間。冬の朝の雨晴海岸から、春の深夜のほたるいか漁へ。 */
  scenery: {
    tour: toyamaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, TOYAMA_FOOD_HULLS),
};
